import React, { useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { AlineAvatar } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembers } from 'src/hooks';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';

export function RequesterFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const [searchTerm, setSearchTerm] = useState('');
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const { orgMembers } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  const options = useMemo(
    () =>
      orgMembers.map(member => ({
        ...member,
        label: member.displayName,
      })),
    [orgMembers]
  );
  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    if (!Array.isArray(rawValues)) return new Set<string>();
    return new Set(rawValues.map((value: { id: string }) => value.id));
  }, [filterModel, field]);

  const handleToggle = (memberId: string) => {
    const currentValues: Array<{
      __typename: 'User';
      displayName: string;
      email: string;
      id: string;
    }> = filterModel.items.find(item => item.field === field)?.value ?? [];
    const isSelected = selectedIds.has(memberId);
    const member = options.find(option => option.id === memberId);
    if (!member) return;

    const newValues = isSelected
      ? currentValues.filter(value => value.id !== memberId)
      : [
          ...currentValues,
          {
            __typename: 'User' as const,
            displayName: member.displayName,
            email: member.email,
            id: member.id,
          },
        ];

    const updatedItems = filterModel.items.filter(item => item.field !== field);
    if (newValues.length > 0) {
      updatedItems.push({ field, operator, value: newValues });
    }
    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      showSearch
      options={options}
      renderOption={option => (
        <>
          <AlineAvatar avatarInfo={option} styles={{ size: 'xs' }} />
          <Typography variant="body2">{option.label}</Typography>
        </>
      )}
      searchPlaceholder="Search requesters..."
      searchTerm={searchTerm}
      selectedIds={selectedIds}
      onSearchChange={setSearchTerm}
      onToggle={handleToggle}
    />
  );
}
